import { memo, useCallback } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { AssetVersionControlRow } from '~/components/ShareModal/components/VersionsAndDownloadingSection/AssetVersionControlRow';
import { DownloadsRow } from '~/components/ShareModal/components/VersionsAndDownloadingSection/DownloadsRow';
import { type OnShortUrlUpdate } from '~/components/ShareModal/types/onShortUrlUpdate';
import { type ShareModalUpdateResponse } from '~/components/ShareModal/types/shareModalUpdateResponse';
import { getDownloadPermissionMode } from '~/utils/shortUrl/buildDownloadPermissionShortUrlUpdate';
import {
  shareUrlUpdatedDownloadsFromMode,
  shareUrlUpdatedVersionControl,
} from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

export type VersionsAndDownloadingSectionProps = {
  shortIdInfo: ShortUrlResponse;
  isLoading: boolean;
  onShortUrlUpdate: OnShortUrlUpdate<ShareModalUpdateResponse>;
  workspaceCanDownload: boolean;
};

export const VersionsAndDownloadingSection = memo(
  ({ shortIdInfo, isLoading, onShortUrlUpdate, workspaceCanDownload }: VersionsAndDownloadingSectionProps) => {
    const handleDownloadsChange = useCallback(
      (next: { canViewOriginal: boolean; hideDownloadAll: boolean }) => {
        const mode = getDownloadPermissionMode(next.canViewOriginal, next.hideDownloadAll);

        void onShortUrlUpdate({
          props: {
            permissions: { asset: { canViewOriginal: next.canViewOriginal } },
            presentationSettings: { hideDownloadAll: next.hideDownloadAll },
          },
          analytics: shareUrlUpdatedDownloadsFromMode(mode),
        });
      },
      [onShortUrlUpdate],
    );

    const handleVersionControlChange = useCallback(
      (canViewAllVersions: boolean) =>
        onShortUrlUpdate({
          props: { permissions: { asset: { canViewAllVersions } } },
          analytics: shareUrlUpdatedVersionControl(canViewAllVersions, 'multi-asset'),
        }),
      [onShortUrlUpdate],
    );

    return (
      <div className="flex flex-col gap-2">
        <ShareModalSectionLabel title="Versions & Downloading" />
        <AssetVersionControlRow
          variant="multi-asset"
          canViewAllVersions={shortIdInfo.permissions.asset.canViewAllVersions}
          data-testid="ASSET_VERSION_CONTROL_SEGMENTED_CONTROL"
          isLoading={isLoading}
          onChange={handleVersionControlChange}
        />
        <DownloadsRow
          canViewOriginal={shortIdInfo.permissions.asset.canViewOriginal}
          data-testid="DOWNLOADS_SEGMENTED_CONTROL"
          hideDownloadAll={shortIdInfo.presentationSettings?.hideDownloadAll}
          isLoading={isLoading}
          onChange={handleDownloadsChange}
          workspaceCanDownload={workspaceCanDownload}
        />
      </div>
    );
  },
);

VersionsAndDownloadingSection.displayName = 'VersionsAndDownloadingSection';
